import React, { useState, useEffect } from 'react';
import {
  Box,
  Stack,
  TextField,
  Button,
  Card,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  CircularProgress,
  Alert,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Avatar,
  Divider,
  Chip,
  Collapse,
} from '@mui/material';
import {
  Schedule as ScheduleIcon,
  Assessment as AssessmentIcon,
  People as PeopleIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Refresh as RefreshIcon,
  FileDownload as DownloadIcon,
} from '@mui/icons-material';
import { formatDate, formatDuration } from './utils';

const DRAWER_WIDTH = 260;

const Employees = ({ user, onLogout, onNavigate }) => {
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [employeeEntries, setEmployeeEntries] = useState([]);
  const [expandedEmployee, setExpandedEmployee] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Date filters for selected employee
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  
  // Employee stats
  const [employeeStats, setEmployeeStats] = useState({
    totalHours: 0,
    billableHours: 0,
    nonBillableHours: 0,
    totalMileage: 0,
    entryCount: 0,
  });

  // Initialize with current month
  useEffect(() => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    
    setStartDate(`${year}-${month}-01`);
    setEndDate(formatDate(today));
  }, []);

  // Load all employees on mount
  useEffect(() => {
    loadAllEmployees();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadAllEmployees = async () => {
    setLoading(true);
    setError('');
    
    try {
      console.log('🔵 [Employees] Loading all employees...');
      
      // Fetch ALL entries from all users with pagination
      let allItems = [];
      let offset = 0;
      const limit = 1000;
      
      while (true) {
        const response = await fetch(
          `https://oracleapex.com/ords/sbas/sbastimedata/?limit=${limit}&offset=${offset}`
        );
        const data = await response.json();
        
        const items = data.items || [];
        console.log(`🔵 [Employees] Fetched ${items.length} entries at offset ${offset}`);
        
        if (items.length === 0) break;
        
        allItems = allItems.concat(items);
        offset += items.length;
        
        if (items.length < limit) break;
      }
      
      console.log(`✅ [Employees] Total entries fetched: ${allItems.length}`);
      
      // Group by employee and calculate stats
      const employeeMap = {};
      
      allItems.forEach(entry => {
        const username = entry.user_ || 'Unknown';
        
        if (!employeeMap[username]) {
          employeeMap[username] = {
            username: username,
            totalHours: 0,
            billableHours: 0,
            nonBillableHours: 0,
            totalMileage: 0,
            entryCount: 0,
            lastEntry: null,
          };
        }
        
        const hours = parseFloat(entry.duration) || 0;
        const mileage = parseFloat(entry.mileage) || 0;
        const isBillable = entry.billable === 'Y' || entry.billable === 'YES';
        
        employeeMap[username].totalHours += hours;
        employeeMap[username].entryCount += 1;
        employeeMap[username].totalMileage += mileage;
        
        if (isBillable) {
          employeeMap[username].billableHours += hours;
        } else {
          employeeMap[username].nonBillableHours += hours;
        }
        
        // Track last entry date
        if (entry.start_date) {
          const entryDate = new Date(entry.start_date);
          if (!employeeMap[username].lastEntry || entryDate > employeeMap[username].lastEntry) {
            employeeMap[username].lastEntry = entryDate;
          }
        }
      });
      
      // Convert to array and sort by total hours
      const employeeList = Object.values(employeeMap).sort((a, b) => b.totalHours - a.totalHours);
      setEmployees(employeeList);
      
      console.log(`✅ [Employees] Found ${employeeList.length} unique employees`);
      
    } catch (err) {
      console.error('❌ [Employees] Error loading employees:', err);
      setError('Failed to load employees: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadEmployeeEntries = async (username) => {
    setLoading(true);
    setError('');
    setSelectedEmployee(username);
    
    try {
      console.log(`🔵 [Employees] Loading entries for ${username}`);
      
      // Fetch ALL entries for this user with pagination
      let allItems = [];
      let offset = 0;
      const limit = 1000;
      
      const encodedUsername = encodeURIComponent(username);
      
      while (true) {
        const response = await fetch(
          `https://oracleapex.com/ords/sbas/sbastimedata/?q={"user_":"${encodedUsername}"}&limit=${limit}&offset=${offset}`
        );
        const data = await response.json();
        
        const items = data.items || [];
        
        if (items.length === 0) break;
        
        allItems = allItems.concat(items);
        offset += items.length;
        
        if (items.length < limit) break;
      }
      
      console.log(`✅ [Employees] Fetched ${allItems.length} entries for ${username}`);
      
      if (allItems.length === 0) {
        setEmployeeEntries([]);
        setEmployeeStats({
          totalHours: 0,
          billableHours: 0,
          nonBillableHours: 0,
          totalMileage: 0,
          entryCount: 0,
        });
        setLoading(false);
        return;
      }
      
      // Filter by date range
      const start = new Date(startDate);
      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999);
      
      const filteredEntries = allItems.filter(entry => {
        if (!entry.start_date) return false;
        const entryDate = new Date(entry.start_date);
        return entryDate >= start && entryDate <= end;
      });
      
      // Sort by date (newest first)
      filteredEntries.sort((a, b) => {
        const dateA = new Date(a.start_date);
        const dateB = new Date(b.start_date);
        return dateB - dateA;
      });
      
      setEmployeeEntries(filteredEntries);
      
      // Calculate stats
      let totalHours = 0;
      let billableHours = 0;
      let nonBillableHours = 0;
      let totalMileage = 0;
      
      filteredEntries.forEach(entry => {
        const hours = parseFloat(entry.duration) || 0;
        const mileage = parseFloat(entry.mileage) || 0;
        const isBillable = entry.billable === 'Y' || entry.billable === 'YES';
        
        totalHours += hours;
        totalMileage += mileage;
        
        if (isBillable) {
          billableHours += hours;
        } else {
          nonBillableHours += hours;
        }
      });
      
      setEmployeeStats({
        totalHours,
        billableHours,
        nonBillableHours,
        totalMileage,
        entryCount: filteredEntries.length,
      });
      
      console.log(`✅ [Employees] Filtered to ${filteredEntries.length} entries in date range`);
      
    } catch (err) {
      console.error('❌ [Employees] Error loading employee entries:', err);
      setError('Failed to load employee entries: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const exportEmployeeData = () => {
    if (!selectedEmployee || employeeEntries.length === 0) {
      alert('No data to export');
      return;
    }

    const headers = ['Date', 'Client', 'Description', 'Start Time', 'End Time', 'Duration', 'Billable', 'Mileage'];
    const rows = employeeEntries.map(entry => [
      entry.start_date ? new Date(entry.start_date).toLocaleDateString() : '',
      entry.client || '',
      (entry.description || '').replace(/"/g, '""'), // Escape quotes
      entry.start_time || '',
      entry.end_time || '',
      entry.duration?.toFixed(2) || '0',
      entry.billable === 'Y' || entry.billable === 'YES' ? 'Yes' : 'No',
      entry.mileage || '0'
    ]);
    
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${selectedEmployee}_timesheet_${startDate}_to_${endDate}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const toggleEmployeeExpanded = (username) => {
    if (expandedEmployee === username) {
      setExpandedEmployee(null);
    } else {
      setExpandedEmployee(username);
      if (selectedEmployee !== username) {
        loadEmployeeEntries(username);
      }
    }
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#f5f7fa' }}>
      {/* Left Sidebar */}
      <Drawer
        variant="permanent"
        sx={{
          width: DRAWER_WIDTH,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: DRAWER_WIDTH,
            boxSizing: 'border-box',
            bgcolor: '#4a5d8f',
            color: 'white',
            borderRight: 'none',
          },
        }}
      >
        {/* Logo/Brand */}
        <Box sx={{ p: 3, pb: 2, textAlign: 'center' }}>
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
              mb: 2,
            }}
          >
            <img
              src="/logo.png"
              alt="SBAS Logo"
              style={{
                width: '180px',
                maxWidth: '100%',
                height: 'auto',
              }}
            />
          </Box>

          <Typography variant="h5" sx={{ fontWeight: 700, color: 'white' }}>
            Time Tracking
          </Typography>

          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
            by SBAS
          </Typography>
        </Box>

        <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)', mx: 2 }} />

        {/* Navigation */}
        <List sx={{ px: 2, py: 3 }}>
          <ListItem
            button
            onClick={() => onNavigate && onNavigate('timesheet')}
            sx={{
              borderRadius: 2,
              mb: 1,
              '&:hover': {
                bgcolor: 'rgba(255,255,255,0.1)',
              },
            }}
          >
            <ListItemIcon sx={{ color: 'rgba(255,255,255,0.7)', minWidth: 40 }}>
              <ScheduleIcon />
            </ListItemIcon>
            <ListItemText
              primary="My Timesheet"
              primaryTypographyProps={{
                fontWeight: 400,
                fontSize: '0.95rem',
                color: 'rgba(255,255,255,0.85)',
              }}
            />
          </ListItem>

          <ListItem
            button
            onClick={() => onNavigate && onNavigate('reports')}
            sx={{
              borderRadius: 2,
              mb: 1,
              '&:hover': {
                bgcolor: 'rgba(255,255,255,0.1)',
              },
            }}
          >
            <ListItemIcon sx={{ color: 'rgba(255,255,255,0.7)', minWidth: 40 }}>
              <AssessmentIcon />
            </ListItemIcon>
            <ListItemText
              primary="Reports"
              primaryTypographyProps={{
                fontWeight: 400,
                fontSize: '0.95rem',
                color: 'rgba(255,255,255,0.85)',
              }}
            />
          </ListItem>

          <ListItem
            button
            selected
            sx={{
              borderRadius: 2,
              mb: 1,
              '&.Mui-selected': {
                bgcolor: 'rgba(255,255,255,0.15)',
              },
            }}
          >
            <ListItemIcon sx={{ color: 'white', minWidth: 40 }}>
              <PeopleIcon />
            </ListItemIcon>
            <ListItemText
              primary="Employees"
              primaryTypographyProps={{
                fontWeight: 500,
                fontSize: '0.95rem',
              }}
            />
          </ListItem>
        </List>

        {/* User Profile at Bottom */}
        <Box sx={{ mt: 'auto', p: 2 }}>
          <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)', mb: 2 }} />
          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar sx={{ width: 40, height: 40, bgcolor: '#5d7ab8' }}>
              {user?.name?.charAt(0) || 'U'}
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: 'white', fontSize: '0.9rem' }}>
                {user?.name || 'User'}
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                {user?.role || 'User'}
              </Typography>
            </Box>
            <IconButton
              onClick={onLogout}
              size="small"
              sx={{
                color: 'rgba(255,255,255,0.7)',
                '&:hover': { color: 'white' },
              }}
            >
              🚪
            </IconButton>
          </Stack>
        </Box>
      </Drawer>

      {/* Main Content Area */}
      <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
        {/* Page Header */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#2c3e50', mb: 1 }}>
            👥 Employee Timesheets
          </Typography>
          <Typography variant="body2" color="text.secondary">
            View detailed timesheets for each employee
          </Typography>
        </Box>

        {/* Error Message */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {/* Summary Cards */}
        <Stack direction="row" spacing={3} sx={{ mb: 4 }} flexWrap="wrap" useFlexGap>
          <Card sx={{ flex: 1, minWidth: 200 }}>
            <Box sx={{ p: 3 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                Total Employees
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#4a5d8f', mt: 1 }}>
                {employees.length}
              </Typography>
            </Box>
          </Card>

          <Card sx={{ flex: 1, minWidth: 200 }}>
            <Box sx={{ p: 3 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                Company Total Hours
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#4caf50', mt: 1 }}>
                {employees.reduce((sum, emp) => sum + emp.totalHours, 0).toFixed(1)}h
              </Typography>
            </Box>
          </Card>

          <Card sx={{ flex: 1, minWidth: 200 }}>
            <Box sx={{ p: 3 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                Total Entries
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#ff9800', mt: 1 }}>
                {employees.reduce((sum, emp) => sum + emp.entryCount, 0)}
              </Typography>
            </Box>
          </Card>
        </Stack>

        {/* Employees List */}
        <Card sx={{ 
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          borderRadius: 3,
          border: '1px solid #e8ecf1',
          overflow: 'hidden'
        }}>
          <Box sx={{ 
            p: 3, 
            bgcolor: '#fafbfc',
            borderBottom: '1px solid #e8ecf1',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#2c3e50' }}>
              All Employees
            </Typography>
            <Button
              variant="outlined"
              size="small"
              startIcon={<RefreshIcon />}
              onClick={loadAllEmployees}
              disabled={loading}
            >
              Refresh
            </Button>
          </Box>

          {loading && !selectedEmployee ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
              <CircularProgress />
            </Box>
          ) : employees.length === 0 ? (
            <Box sx={{ p: 6, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                No employees found
              </Typography>
            </Box>
          ) : (
            employees.map((employee) => (
              <Box key={employee.username}>
                {/* Employee Summary Row */}
                <Box
                  onClick={() => toggleEmployeeExpanded(employee.username)}
                  sx={{
                    p: 3,
                    borderBottom: '1px solid #f0f2f5',
                    cursor: 'pointer',
                    transition: 'background-color 0.15s ease',
                    '&:hover': {
                      bgcolor: '#fafbfc',
                    },
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Avatar sx={{ width: 48, height: 48, bgcolor: '#4a5d8f', fontWeight: 600 }}>
                      {employee.username.charAt(0).toUpperCase()}
                    </Avatar>
                    
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, color: '#2c3e50' }}>
                        {employee.username}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {employee.entryCount} entries • Last entry: {employee.lastEntry ? employee.lastEntry.toLocaleDateString() : 'N/A'}
                      </Typography>
                    </Box>

                    <Chip 
                      label={`${employee.totalHours.toFixed(1)}h total`}
                      sx={{ bgcolor: '#e3f2fd', color: '#1976d2', fontWeight: 600 }}
                    />
                    
                    <Chip 
                      label={`${employee.billableHours.toFixed(1)}h billable`}
                      sx={{ bgcolor: '#e8f5e9', color: '#4caf50', fontWeight: 600 }}
                    />

                    {expandedEmployee === employee.username ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </Stack>
                </Box>

                {/* Employee Detail (Expanded) */}
                <Collapse in={expandedEmployee === employee.username}>
                  <Box sx={{ bgcolor: '#fafbfc', p: 3, borderBottom: '1px solid #e8ecf1' }}>
                    {/* Date Range Filters */}
                    <Stack direction="row" spacing={2} sx={{ mb: 3 }} alignItems="center" flexWrap="wrap">
                      <TextField
                        type="date"
                        label="Start Date"
                        size="small"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                      />
                      <TextField
                        type="date"
                        label="End Date"
                        size="small"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                      />
                      <Button
                        variant="contained"
                        onClick={() => loadEmployeeEntries(employee.username)}
                        disabled={loading}
                      >
                        Load Entries
                      </Button>
                      {selectedEmployee === employee.username && employeeEntries.length > 0 && (
                        <Button
                          variant="outlined"
                          startIcon={<DownloadIcon />}
                          onClick={exportEmployeeData}
                        >
                          Export CSV
                        </Button>
                      )}
                    </Stack>

                    {/* Statistics */}
                    {selectedEmployee === employee.username && (
                      <Stack direction="row" spacing={2} sx={{ mb: 3 }} flexWrap="wrap">
                        <Card sx={{ flex: 1, minWidth: 150 }}>
                          <Box sx={{ p: 2 }}>
                            <Typography variant="caption" color="text.secondary">Total Hours</Typography>
                            <Typography variant="h6" sx={{ fontWeight: 700, color: '#4a5d8f' }}>
                              {employeeStats.totalHours.toFixed(2)}h
                            </Typography>
                          </Box>
                        </Card>
                        <Card sx={{ flex: 1, minWidth: 150 }}>
                          <Box sx={{ p: 2 }}>
                            <Typography variant="caption" color="text.secondary">Billable</Typography>
                            <Typography variant="h6" sx={{ fontWeight: 700, color: '#4caf50' }}>
                              {employeeStats.billableHours.toFixed(2)}h
                            </Typography>
                          </Box>
                        </Card>
                        <Card sx={{ flex: 1, minWidth: 150 }}>
                          <Box sx={{ p: 2 }}>
                            <Typography variant="caption" color="text.secondary">Non-Billable</Typography>
                            <Typography variant="h6" sx={{ fontWeight: 700, color: '#ff9800' }}>
                              {employeeStats.nonBillableHours.toFixed(2)}h
                            </Typography>
                          </Box>
                        </Card>
                        <Card sx={{ flex: 1, minWidth: 150 }}>
                          <Box sx={{ p: 2 }}>
                            <Typography variant="caption" color="text.secondary">Entries</Typography>
                            <Typography variant="h6" sx={{ fontWeight: 700, color: '#757575' }}>
                              {employeeStats.entryCount}
                            </Typography>
                          </Box>
                        </Card>
                      </Stack>
                    )}

                    {/* Entries Table */}
                    {loading && selectedEmployee === employee.username ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                        <CircularProgress size={32} />
                      </Box>
                    ) : selectedEmployee === employee.username && employeeEntries.length === 0 ? (
                      <Box sx={{ textAlign: 'center', p: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                          No entries found for selected date range
                        </Typography>
                      </Box>
                    ) : selectedEmployee === employee.username && (
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow sx={{ bgcolor: '#f8f9fb' }}>
                              <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Client</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Time</TableCell>
                              <TableCell sx={{ fontWeight: 600 }} align="right">Duration</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Billable</TableCell>
                              <TableCell sx={{ fontWeight: 600 }} align="right">Mileage</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {employeeEntries.map((entry) => (
                              <TableRow key={entry.timedata_id_pk} sx={{ '&:hover': { bgcolor: '#fafbfc' } }}>
                                <TableCell>
                                  {entry.start_date ? new Date(entry.start_date).toLocaleDateString() : ''}
                                </TableCell>
                                <TableCell>{entry.client}</TableCell>
                                <TableCell>
                                  {entry.description?.substring(0, 50)}{entry.description?.length > 50 ? '...' : ''}
                                </TableCell>
                                <TableCell>{entry.start_time} - {entry.end_time}</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 600, color: '#4a5d8f' }}>
                                  {formatDuration(entry.duration || 0)}
                                </TableCell>
                                <TableCell>
                                  {entry.billable === 'Y' || entry.billable === 'YES' ? (
                                    <Chip label="Yes" size="small" color="success" />
                                  ) : (
                                    <Chip label="No" size="small" />
                                  )}
                                </TableCell>
                                <TableCell align="right">{entry.mileage || 0} km</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </Box>
                </Collapse>
              </Box>
            ))
          )}
        </Card>
      </Box>
    </Box>
  );
};

export default Employees;
